import CancelablePromise, { cancelable } from 'cancelable-promise';
import { ceil, divide } from 'lodash';
import { Inject, Service, StatefulService } from 'react-service-locator';
import { toEnglish } from '../translation';
import { jsonDateParser } from 'json-date-parser';
import { LifestyleInterventionService } from './lifestyle-intervention.service';
import { DiseaseService } from './disease.service';
import delay from 'delay';
import StatusCode from 'status-code-enum';
import { refreshTokensIfNeeded } from '../axios/axiosAuthInterceptor';
import { trackSiteSearch } from '../analytics';

type Gender = 'male' | 'female';
type StudyDesign = 'meta-analysis' | 'RCT';
export type SortEnum = 'score_asc' | 'score_desc';

type IFilters = {
    minAge: number;
    maxAge: number;
    gender?: Gender[];
    studyDesign?: StudyDesign[];
    minPublicationYear: number;
    maxPublicationYear: number;
    studyGroupSize?: number[];
    studyPeriod?: number[];
    amountOfCitations?: number[];
    ethnicity?: (
        | 'NorthernAfrican'
        | 'SouthernEuropean'
        | 'CentralEuropean'
        | 'Asian'
        | 'SouthAfrican'
        | 'SouthAsian'
        | 'WesternEuropean'
        | 'SouthwestAsian'
        | 'LatinAmerican'
        | 'WestAfrican'
        | 'NorthernEuropean'
        | 'Oceanian'
        | 'EastAfrican'
        | 'Balkan'
        | 'HornOfAfrica'
        | 'NorthAmerican'
        | 'CentralAfrican'
        | 'Baltic'
        | 'EasternEuropean'
        | 'Caucasian'
    )[];
    countries?: string[];
};
export interface IPaginatedResult {
    items: IPage;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

type IPage = IArticle[];

export interface IArticle {
    pmid: string;
    dois: string[];
    title: string;
    score: number;
    keywords: string[];
    publicationDate?: Date;
    indicators: string[];
    isFunded: boolean;
    researchPopulation?: string;
}

type ISearchCriteria = {
    keywords: string[];
    lifestyleInterventions: string[];
};

type ISearchQuery = {
    criteria: ISearchCriteria;
    filters: Partial<IFilters>;
    sort: SortEnum;
    pageNumber: number;
    pageSize: number;
};

type IPagination = {
    TotalCount: number;
};

@Service()
export class SearchService extends StatefulService<{
    disease?: string;
    subcategories: string[];
    symptoms: string[];
    lifestyleInterventionGroup?: string;
    lifestyleInterventions: string[];
    lifestyleInterventionGroupAll: boolean;
    filters: IFilters;
    isLoading: boolean;
    result?: IPaginatedResult;
    sort: SortEnum;
}> {
    @Inject(LifestyleInterventionService)
    private readonly lifestyleInterventionService!: LifestyleInterventionService;
    @Inject(DiseaseService)
    private readonly diseaseService!: DiseaseService;
    public static MIN_AGE = 0;
    public static MAX_AGE = 120;
    public static MIN_PUBLICATION_YEAR = 1901;
    public static get MAX_PUBLICATION_YEAR() {
        return new Date().getFullYear();
    }
    private tasks: CancelablePromise[];

    public constructor() {
        super({
            filters: {
                minAge: SearchService.MIN_AGE,
                maxAge: SearchService.MAX_AGE,
                minPublicationYear: 2000,
                maxPublicationYear: SearchService.MAX_PUBLICATION_YEAR,
                gender: undefined,
                studyDesign: undefined,
                studyGroupSize: undefined,
                studyPeriod: undefined,
                amountOfCitations: undefined,
                ethnicity: undefined,
                countries: undefined,
            },
            sort: 'score_desc',
            lifestyleInterventions: [],
            subcategories: [],
            symptoms: [],
            lifestyleInterventionGroupAll: false,
            isLoading: false,
        });
        this.tasks = [];
    }

    private async loadPages(pageNumber: number) {
        const disease = (this.state.disease ?? '')
            .trim()
            .toLowerCase()
            .replace(/ disease$/, '')
            .replace(/ disease/g, '');

        const all = [this.state.disease, ...(this.state.subcategories ?? []), ...(this.state.symptoms ?? [])];

        const english_keywords: string[] = await Promise.all(
            all
                .filter(Boolean)
                .map((k) => String(k).trim())
                .filter((k, i, arr) => i === 0 || k.toLowerCase() !== disease)
                .map(async (k: string, index: number) => {
                    let cleaned = k;
                    if (index > 0 && k.toLowerCase().startsWith(disease)) {
                        cleaned = k.slice(this.state.disease!.length).trim();
                    }

                    const translated = await toEnglish(cleaned);
                    return translated.match(/^.*?(?=&|$)/)?.[0]?.trim() ?? '';
                }),
        );

        const lifestyleInterventions = this.state.lifestyleInterventions.flatMap((c) => c.split(' ')).filter(Boolean);

        const accessToken = localStorage.getItem('accessToken');
        const date = new Date().toUTCString();
        const base = process.env.REACT_APP_BASE_API_URI ?? '';

        if (lifestyleInterventions.length > 0) {
            fetch(base + '/Document/search/track', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-ms-date': date,
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                },
                body: JSON.stringify({ lifestyleInterventions }),
            }).catch(() => {
                console.log('Failed to track search');
            });
        }

        // Search (GET)
        const params = new URLSearchParams();

        for (const k of english_keywords.filter(Boolean)) params.append('keywords', k);
        for (const li of lifestyleInterventions) params.append('lifestyleInterventions', li);

        // Filters (only set when not default like you already do)
        if (this.state.filters.gender?.length) {
            for (const g of this.state.filters.gender) params.append('gender', g);
        }
        if (this.state.filters.studyDesign?.length) {
            for (const d of this.state.filters.studyDesign) params.append('studyDesign', d);
        }
        if (this.state.filters.ethnicity?.length) {
            for (const e of this.state.filters.ethnicity) params.append('ethnicity', e);
        }
        if (this.state.filters.studyGroupSize?.length) {
            for (const s of this.state.filters.studyGroupSize) params.append('studyGroupSize', String(s));
        }
        if (this.state.filters.studyPeriod?.length) {
            for (const p of this.state.filters.studyPeriod) params.append('studyPeriod', String(p));
        }
        if (this.state.filters.amountOfCitations?.length) {
            for (const c of this.state.filters.amountOfCitations) params.append('amountOfCitation', String(c));
        }

        if (
            this.state.filters.minAge !== SearchService.MIN_AGE ||
            this.state.filters.maxAge !== SearchService.MAX_AGE
        ) {
            params.set('minAge', String(this.state.filters.minAge));
            params.set('maxAge', String(this.state.filters.maxAge));
        }

        if (
            this.state.filters.minPublicationYear !== SearchService.MIN_PUBLICATION_YEAR ||
            this.state.filters.maxPublicationYear !== SearchService.MAX_PUBLICATION_YEAR
        ) {
            params.set('minPublicationYear', String(this.state.filters.minPublicationYear));
            params.set('maxPublicationYear', String(this.state.filters.maxPublicationYear));
        }

        params.set('sort', this.state.sort);
        params.set('pageNumber', String(pageNumber));
        params.set('pageSize', '10');

        return fetch(base + '/Document/search?' + params.toString(), {
            method: 'GET',
            headers: {
                'x-ms-date': date,
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
        });
    }

    public toggleLifestyleInterventionGroupAll(lifestyleInterventionGroupAll: boolean) {
        this.setState({ lifestyleInterventionGroupAll });

        if (lifestyleInterventionGroupAll) {
            const group = this.state.lifestyleInterventionGroup;
            if (group) {
                const key = `${group[0].toUpperCase()}${group.slice(1)}_All`;
                this.setLifestyleInterventions([key]);
            }
        } else {
            this.setLifestyleInterventions([]);
        }
    }

    public setLifestyleInterventions(lifestyleInterventions: string[]) {
        const group = this.state.lifestyleInterventionGroup;
        const allKey = group ? `${group[0].toUpperCase()}${group.slice(1)}_All` : undefined;

        if (lifestyleInterventions.length === 1 && lifestyleInterventions[0] === allKey) {
            this.setState({
                lifestyleInterventionGroupAll: true,
                lifestyleInterventions: [allKey],
            });
            return;
        }

        if (this.state.lifestyleInterventionGroupAll) {
            const filtered = lifestyleInterventions.filter((i) => i !== allKey);

            this.setState({
                lifestyleInterventionGroupAll: false,
                lifestyleInterventions: filtered,
            });
            return;
        }

        const filtered = lifestyleInterventions.filter((i) => i !== allKey);

        this.setState({
            lifestyleInterventionGroupAll: false,
            lifestyleInterventions: filtered,
        });
    }

    public setLifestyleInterventionGroup(lifestyleInterventionGroup?: string) {
        this.setLifestyleInterventions([]);
        this.toggleLifestyleInterventionGroupAll(false);
        this.lifestyleInterventionService.setGroup(lifestyleInterventionGroup);
        this.setState({ lifestyleInterventionGroup });
    }

    public setDisease(disease?: string) {
        this.diseaseService.setDisease(disease);
        this.setSubcategories([]);
        this.setSymptoms([]);
        this.setLifestyleInterventionGroup();
        this.setState({ disease });
    }

    public setSubcategories(subcategories: string[]) {
        this.setState({ subcategories });
    }

    public setSymptoms(symptoms: string[]) {
        this.setState({ symptoms });
    }

    public setFilters(filters: Readonly<IFilters>) {
        this.setState({
            filters: {
                ...filters,
                gender: filters.gender ? [...filters.gender] : undefined,
                studyDesign: filters.studyDesign ? [...filters.studyDesign] : undefined,
                studyGroupSize: filters.studyGroupSize ? [...filters.studyGroupSize] : undefined,
                studyPeriod: filters.studyPeriod ? [...filters.studyPeriod] : undefined,
                amountOfCitations: filters.amountOfCitations ? [...filters.amountOfCitations] : undefined,
                ethnicity: filters.ethnicity ? [...filters.ethnicity] : undefined,
                countries: filters.countries ? [...filters.countries] : undefined,
            },
        });

        // debounce search on filters like before
        this.cancelTasks();
        const task = cancelable(delay(700));
        task.then(() => this.fetchPage(1));
        this.tasks = [task];
    }

    private cancelTasks() {
        this.tasks.forEach((t) => t.cancel());
        this.tasks = [];
    }

    public setSort(sort: SortEnum) {
        this.setState({ sort });
        this.search();
    }

    public clearResult() {
        this.setState({ result: undefined });
    }

    private async fetchPage(pageNumber: number) {
        this.setState({ isLoading: true });

        const response = await this.loadPages(pageNumber);

        if (response.status === StatusCode.SuccessNoContent) {
            this.setState({
                result: {
                    items: [],
                    pageNumber: 1,
                    pageSize: 10,
                    totalCount: 0,
                    totalPages: 0,
                    hasNext: false,
                    hasPrevious: false,
                },
                isLoading: false,
            });
            return;
        }

        const data = (await response.json()) as {
            items: IPage;
            pageNumber: number;
            pageSize: number;
            totalCount: number;
            totalPages: number;
            hasNext: boolean;
            hasPrevious: boolean;
        };

        this.setState({
            result: {
                items: data.items,
                pageNumber: data.pageNumber,
                pageSize: data.pageSize,
                totalCount: data.totalCount,
                totalPages: data.totalPages,
                hasNext: data.hasNext,
                hasPrevious: data.hasPrevious,
            },
            isLoading: false,
        });
    }

    public search() {
        this.setState({ isLoading: true });
        this.fetchPage(1);
    }

    public gotoPage(pageNumber: number) {
        if (!this.state.result) return;
        if (pageNumber === this.state.result.pageNumber) return;
        if (pageNumber < 1 || pageNumber > this.state.result.totalPages) return;
        this.fetchPage(pageNumber);
    }

    public get shouldRedirect() {
        return (
            !this.state.disease ||
            (!this.state.lifestyleInterventionGroupAll && this.state.lifestyleInterventions.length === 0)
        );
    }
}
